import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('customerToken'));
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Load customer on mount
  useEffect(() => {
    if (token) {
      loadCustomer();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadCustomer = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customers/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomer(response.data);
    } catch (error) {
      console.error('Failed to load customer:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const register = async (name, email, password, phone, city) => {
    const response = await axios.post(`${BACKEND_URL}/api/customers/register`, {
      name,
      email,
      password,
      phone,
      city
    });
    
    // Auto login after register
    return login(email, password);
  };

  const login = async (email, password) => {
    const response = await axios.post(`${BACKEND_URL}/api/customers/login`, {
      email,
      password
    });
    
    const { token: newToken, customer: customerData } = response.data;
    setToken(newToken);
    setCustomer(customerData);
    localStorage.setItem('customerToken', newToken);
    
    return customerData;
  };

  const logout = () => {
    setToken(null);
    setCustomer(null);
    localStorage.removeItem('customerToken');
  };

  const updateProfile = async (updates) => {
    const response = await axios.put(
      `${BACKEND_URL}/api/customers/profile`,
      updates,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setCustomer(response.data);
    return response.data;
  };

  const value = {
    customer,
    token,
    loading,
    isAuthenticated: !!customer,
    register,
    login,
    logout,
    updateProfile,
    loadCustomer
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
